/**
 * Part library (PRD §3.5, §5.3, §6.3 — MagicaVoxel reference aesthetic).
 *
 * Voxels are authored on a 4×-resolution grid relative to the original
 * P3 scaffold (1 voxel = 0.025 world units; 40 voxels = 1m). Higher
 * resolution lets us carry:
 *   - chest grilles (multiple vertical slats vs a single chevron block)
 *   - 1-voxel-wide panel seams in the secondary slot along chassis edges
 *   - corner chamfering on chassis tops + shoulders
 *   - knee/elbow servo detail with emissive seam plates
 *   - multi-stripe visors + tapered antennas
 *
 * Scale convention: a typical mech spans ~48 voxels (1.2m) tall, ~24
 * voxels (0.6m) wide. Parts compose along the Y axis: legs anchor at
 * y=-24, chassis at y=0, head at y=28, mounts at y=28+.
 *
 * Body plan references the user's MagicaVoxel inspiration: olive
 * chassis, dark joints, bright orange chevron emissives, dark panel
 * lines along major seams.
 */

import type { PartLibrary, Voxel, VoxelPart } from "../types";

// ─── Primitive helpers ─────────────────────────────────────────────────────

type Slot = Voxel["slot"];

/** Solid box of `slot` voxels. */
function box(
  x0: number,
  y0: number,
  z0: number,
  w: number,
  h: number,
  d: number,
  slot: Slot,
): Voxel[] {
  const out: Voxel[] = [];
  for (let x = x0; x < x0 + w; x++) {
    for (let y = y0; y < y0 + h; y++) {
      for (let z = z0; z < z0 + d; z++) {
        out.push({ x, y, z, slot });
      }
    }
  }
  return out;
}

/**
 * Box with darker 1-voxel-thick seam frame around the four vertical
 * faces (front/back/left/right). Used for chassis bulk so we get the
 * panel-line look from the reference image.
 */
function framedBox(
  x0: number,
  y0: number,
  z0: number,
  w: number,
  h: number,
  d: number,
  fillSlot: Slot,
  seamSlot: Slot,
): Voxel[] {
  const out: Voxel[] = [];
  for (let x = x0; x < x0 + w; x++) {
    for (let y = y0; y < y0 + h; y++) {
      for (let z = z0; z < z0 + d; z++) {
        const onEdge =
          x === x0 || x === x0 + w - 1 || z === z0 || z === z0 + d - 1;
        out.push({ x, y, z, slot: onEdge ? seamSlot : fillSlot });
      }
    }
  }
  return out;
}

/**
 * Top-chamfered box — removes 1-voxel corner cubes at the top four
 * corners, then 1-voxel cubes one step in (slight bevel). Reads as a
 * subtle weld bevel from any angle without burning a lot of voxels.
 */
function chamferedBox(
  x0: number,
  y0: number,
  z0: number,
  w: number,
  h: number,
  d: number,
  slot: Slot,
): Voxel[] {
  const skip = new Set<string>();
  const yTop = y0 + h - 1;
  for (const [dx, dz] of [
    [0, 0],
    [w - 1, 0],
    [0, d - 1],
    [w - 1, d - 1],
  ]) {
    skip.add(`${x0 + dx},${yTop},${z0 + dz}`);
  }
  const out: Voxel[] = [];
  for (let x = x0; x < x0 + w; x++) {
    for (let y = y0; y < y0 + h; y++) {
      for (let z = z0; z < z0 + d; z++) {
        if (skip.has(`${x},${y},${z}`)) continue;
        out.push({ x, y, z, slot });
      }
    }
  }
  return out;
}

/**
 * Vertical-slat grille — `count` emissive slats with `gap` voxels of
 * fill (or no fill if zero) between them. Used for the chest panel.
 */
function vSlatGrille(
  xCenter: number,
  yLo: number,
  zFront: number,
  width: number,
  height: number,
  count: number,
): Voxel[] {
  const out: Voxel[] = [];
  const stride = Math.max(1, Math.floor(width / count));
  const lo = xCenter - Math.floor(width / 2);
  for (let i = 0; i < count; i++) {
    const x = lo + i * stride;
    for (let y = yLo; y < yLo + height; y++) {
      out.push({ x, y, z: zFront, slot: "emissive" });
    }
  }
  return out;
}

/** Horizontal-stripe vent — `count` lines of emissive voxels. */
function hStripeVent(
  x0: number,
  yLo: number,
  zFront: number,
  width: number,
  count: number,
  step: number,
): Voxel[] {
  const out: Voxel[] = [];
  for (let i = 0; i < count; i++) {
    const y = yLo + i * step;
    for (let x = x0; x < x0 + width; x++) {
      out.push({ x, y, z: zFront, slot: "emissive" });
    }
  }
  return out;
}

function concat(...arr: Voxel[][]): Voxel[] {
  const out: Voxel[] = [];
  for (const a of arr) out.push(...a);
  return out;
}

// ─── Chassis variants ─────────────────────────────────────────────────────
// All chassis center on (x=0, z=0), bottom at y=0. Width ranges from
// 16 (light) to 36 (heavy). Total height 20-28 including shoulder mass.

const chassisStandard: VoxelPart = {
  id: "chassis_standard",
  anchor: { x: 0, y: 0, z: 0 },
  voxels: concat(
    // Waist (lower torso) — primary fill with secondary seams
    framedBox(-10, 0, -8, 20, 6, 16, "primary", "accent"),
    // Joint band between waist + chest — dark
    box(-10, 6, -8, 20, 2, 16, "secondary"),
    // Chest — primary with seams, top chamfer
    chamferedBox(-10, 8, -8, 20, 10, 16, "primary"),
    // Vertical seam down chest center
    box(0, 8, 8, 1, 10, 1, "secondary"),
    // Side trim panels
    box(-10, 9, -8, 1, 8, 16, "accent"),
    box(9, 9, -8, 1, 8, 16, "accent"),
    // Shoulder mass — wider than chest, beveled
    chamferedBox(-13, 18, -8, 26, 6, 16, "primary"),
    // Shoulder edge seams
    box(-13, 18, -8, 1, 6, 16, "secondary"),
    box(12, 18, -8, 1, 6, 16, "secondary"),
    // Top vent grille (back)
    box(-3, 23, -8, 6, 1, 1, "secondary"),
    // Chest emissive — 5-slat vertical grille
    vSlatGrille(0, 11, 8, 13, 5, 5),
    // Two side intake louvers (emissive accents)
    hStripeVent(-9, 11, 8, 3, 3, 2),
    hStripeVent(7, 11, 8, 3, 3, 2),
    // Reactor heat vent in upper back
    box(-2, 21, -9, 4, 2, 1, "emissive"),
  ),
};

const chassisHeavy: VoxelPart = {
  id: "chassis_heavy",
  anchor: { x: 0, y: 0, z: 0 },
  voxels: concat(
    // Wider waist
    framedBox(-14, 0, -10, 28, 6, 20, "primary", "accent"),
    // Heavy belt seam
    box(-14, 6, -10, 28, 2, 20, "secondary"),
    // Chest — taller + deeper
    chamferedBox(-14, 8, -10, 28, 12, 20, "primary"),
    // Vertical center beam
    box(-1, 8, 10, 2, 12, 1, "secondary"),
    // Edge seams along chest
    box(-14, 8, -10, 1, 12, 20, "secondary"),
    box(13, 8, -10, 1, 12, 20, "secondary"),
    // Bulky shoulder block — broader
    chamferedBox(-17, 20, -10, 34, 8, 20, "primary"),
    // Shoulder seams
    box(-17, 20, -10, 1, 8, 20, "secondary"),
    box(16, 20, -10, 1, 8, 20, "secondary"),
    // Top vent line
    box(-4, 27, -10, 8, 1, 1, "secondary"),
    // Big 7-slat chest grille
    vSlatGrille(0, 11, 10, 21, 7, 7),
    // Reactor bulge — back of upper torso
    box(-3, 18, -11, 6, 4, 1, "emissive"),
    // Belly armored skirt — accent stripe
    box(-14, 1, -10, 28, 1, 20, "accent"),
  ),
};

const chassisLight: VoxelPart = {
  id: "chassis_light",
  anchor: { x: 0, y: 0, z: 0 },
  voxels: concat(
    // Slim waist
    framedBox(-8, 0, -6, 16, 5, 12, "primary", "accent"),
    box(-8, 5, -6, 16, 1, 12, "secondary"),
    // Narrow chest
    chamferedBox(-10, 6, -6, 20, 10, 12, "primary"),
    // Side stripes
    box(-10, 7, -6, 1, 8, 12, "accent"),
    box(9, 7, -6, 1, 8, 12, "accent"),
    // Modest shoulder block
    chamferedBox(-11, 16, -6, 22, 4, 12, "primary"),
    box(-11, 16, -6, 1, 4, 12, "secondary"),
    box(10, 16, -6, 1, 4, 12, "secondary"),
    // Three-slat lean chest grille
    vSlatGrille(0, 9, 6, 9, 4, 3),
    // Side intake slits
    hStripeVent(-9, 8, 6, 2, 3, 2),
    hStripeVent(7, 8, 6, 2, 3, 2),
    // Visible heat vent stripe under arms
    box(-3, 14, -7, 6, 1, 1, "emissive"),
  ),
};

// ─── Legs variants ────────────────────────────────────────────────────────
// Legs anchor below the chassis (y=-24 origin). Foot pad at the bottom
// roughly y=0..3, shin y=3..14, knee y=14..16, thigh y=16..23, hip
// y=23..24 connecting to chassis.

const legsBiped: VoxelPart = {
  id: "legs_biped",
  anchor: { x: 0, y: -24, z: 0 },
  voxels: concat(
    // Left leg
    // Foot pad
    box(-8, 0, -6, 6, 2, 12, "secondary"),
    box(-8, 2, -6, 6, 1, 12, "accent"), // top trim of foot
    // Shin
    framedBox(-7, 3, -4, 4, 11, 8, "primary", "secondary"),
    // Shin emissive cable strip
    box(-5, 4, -5, 2, 10, 1, "emissive"),
    // Knee servo (cylinder-ish — wider band)
    box(-8, 14, -4, 6, 2, 8, "secondary"),
    box(-8, 15, -3, 6, 1, 1, "emissive"),
    // Thigh
    framedBox(-7, 16, -4, 4, 7, 8, "primary", "secondary"),
    // Thigh accent
    box(-7, 17, 4, 4, 5, 1, "accent"),

    // Right leg (mirror across X=0)
    box(2, 0, -6, 6, 2, 12, "secondary"),
    box(2, 2, -6, 6, 1, 12, "accent"),
    framedBox(3, 3, -4, 4, 11, 8, "primary", "secondary"),
    box(3, 4, -5, 2, 10, 1, "emissive"),
    box(2, 14, -4, 6, 2, 8, "secondary"),
    box(2, 15, -3, 6, 1, 1, "emissive"),
    framedBox(3, 16, -4, 4, 7, 8, "primary", "secondary"),
    box(3, 17, 4, 4, 5, 1, "accent"),

    // Pelvis bar bridging the two legs
    box(-3, 23, -4, 6, 1, 8, "secondary"),
    box(-3, 23, -4, 6, 1, 1, "emissive"),
  ),
};

const legsReverseKnee: VoxelPart = {
  id: "legs_reverse_knee",
  anchor: { x: 0, y: -24, z: 0 },
  voxels: concat(
    // Talon feet, splayed
    box(-9, 0, -2, 8, 2, 6, "secondary"),
    box(-9, 0, 4, 8, 1, 4, "secondary"), // front toes
    box(1, 0, -2, 8, 2, 6, "secondary"),
    box(1, 0, 4, 8, 1, 4, "secondary"),

    // Forward-canted shin (bottom forward, top back)
    framedBox(-7, 3, 0, 4, 8, 6, "primary", "secondary"),
    framedBox(3, 3, 0, 4, 8, 6, "primary", "secondary"),
    // Cable channels emissive
    box(-5, 4, 6, 2, 6, 1, "emissive"),
    box(3, 4, 6, 2, 6, 1, "emissive"),

    // Reverse knee joint — emissive seam (bigger than biped's)
    box(-7, 11, -2, 4, 2, 8, "secondary"),
    box(3, 11, -2, 4, 2, 8, "secondary"),
    box(-7, 12, 5, 4, 1, 1, "emissive"),
    box(3, 12, 5, 4, 1, 1, "emissive"),

    // Back-canted thigh (top back, bottom forward already from knee)
    framedBox(-7, 13, -4, 4, 9, 6, "primary", "secondary"),
    framedBox(3, 13, -4, 4, 9, 6, "primary", "secondary"),
    box(-5, 14, -5, 2, 7, 1, "accent"),
    box(3, 14, -5, 2, 7, 1, "accent"),

    // Hip joint
    box(-3, 22, -3, 6, 2, 6, "secondary"),
    box(-1, 23, -3, 2, 1, 1, "emissive"),
  ),
};

const legsHeavy: VoxelPart = {
  id: "legs_heavy",
  anchor: { x: 0, y: -20, z: 0 },
  voxels: concat(
    // Wide armored feet
    box(-12, 0, -8, 10, 2, 16, "secondary"),
    box(-12, 2, -8, 10, 1, 16, "accent"),
    box(2, 0, -8, 10, 2, 16, "secondary"),
    box(2, 2, -8, 10, 1, 16, "accent"),

    // Thick shins
    framedBox(-10, 3, -6, 6, 9, 12, "primary", "secondary"),
    framedBox(4, 3, -6, 6, 9, 12, "primary", "secondary"),
    // Front armor plate
    box(-8, 4, 6, 4, 7, 1, "accent"),
    box(4, 4, 6, 4, 7, 1, "accent"),
    // Inner-leg reactor glow
    box(-5, 5, -6, 1, 5, 12, "emissive"),
    box(4, 5, -6, 1, 5, 12, "emissive"),

    // Knee armor block
    box(-10, 12, -6, 6, 2, 12, "secondary"),
    box(4, 12, -6, 6, 2, 12, "secondary"),
    box(-10, 13, 5, 6, 1, 1, "emissive"),
    box(4, 13, 5, 6, 1, 1, "emissive"),

    // Thick thighs
    framedBox(-10, 14, -6, 6, 5, 12, "primary", "secondary"),
    framedBox(4, 14, -6, 6, 5, 12, "primary", "secondary"),

    // Hip bridge — heavier than biped
    box(-4, 19, -6, 8, 1, 12, "secondary"),
    box(-2, 19, -6, 4, 1, 1, "emissive"),
  ),
};

// ─── Arm variants ─────────────────────────────────────────────────────────
// Right-arm-by-convention. Composer instantiates both shoulders; we
// share the same data for left + right for now (mirror pass lands as
// a P5 follow-up if needed). Anchor places the shoulder ball flush
// against the chassis side at the shoulder block height.

const armRifle: VoxelPart = {
  id: "arm_rifle",
  anchor: { x: 16, y: 16, z: 0 },
  voxels: concat(
    // Shoulder ball (dark)
    box(0, 0, -4, 4, 8, 8, "secondary"),
    // Shoulder cap (primary)
    box(0, 6, -4, 4, 2, 8, "primary"),
    // Upper arm
    framedBox(0, -8, -4, 4, 8, 8, "primary", "secondary"),
    // Elbow joint
    box(0, -10, -4, 4, 2, 8, "secondary"),
    box(0, -9, 4, 4, 1, 1, "emissive"),
    // Forearm + rifle body
    framedBox(0, -12, 0, 4, 4, 12, "primary", "secondary"),
    // Rifle barrel (slim cylinder approximated by a 2×2×12 column)
    box(1, -11, 12, 2, 2, 12, "secondary"),
    // Barrel detail — every-third-voxel accent ring
    box(1, -11, 14, 2, 2, 1, "accent"),
    box(1, -11, 18, 2, 2, 1, "accent"),
    box(1, -11, 22, 2, 2, 1, "accent"),
    // Muzzle (emissive)
    box(1, -11, 24, 2, 2, 2, "emissive"),
    // Sight rail on top of rifle body
    box(1, -8, 4, 2, 1, 6, "secondary"),
    box(1, -8, 6, 2, 1, 2, "emissive"),
  ),
};

const armCannon: VoxelPart = {
  id: "arm_cannon",
  anchor: { x: 16, y: 16, z: 0 },
  voxels: concat(
    // Heavier shoulder
    box(0, 0, -5, 5, 8, 10, "secondary"),
    box(0, 6, -5, 5, 2, 10, "primary"),
    // Bulky upper arm
    framedBox(0, -8, -5, 5, 8, 10, "primary", "secondary"),
    // Elbow servo
    box(0, -10, -5, 5, 2, 10, "secondary"),
    box(0, -9, 5, 5, 1, 1, "emissive"),
    // Cannon body — taller + wider than rifle
    framedBox(0, -12, 0, 5, 6, 12, "primary", "secondary"),
    // Ammo drum on top
    box(0, -6, -2, 5, 4, 4, "accent"),
    box(1, -5, -3, 3, 2, 1, "secondary"),
    // Cannon barrel — thick
    box(1, -11, 12, 3, 3, 14, "secondary"),
    // Recoil flutes (accent rings)
    box(1, -11, 15, 3, 3, 1, "accent"),
    box(1, -11, 20, 3, 3, 1, "accent"),
    // Massive muzzle brake
    box(0, -12, 26, 5, 5, 2, "primary"),
    box(1, -11, 26, 3, 3, 2, "emissive"),
  ),
};

const armBeam: VoxelPart = {
  id: "arm_beam",
  anchor: { x: 16, y: 16, z: 0 },
  voxels: concat(
    // Shoulder
    box(0, 0, -4, 4, 8, 8, "secondary"),
    box(0, 6, -4, 4, 2, 8, "primary"),
    // Slim upper arm
    framedBox(0, -8, -3, 4, 8, 6, "primary", "secondary"),
    // Elbow seam
    box(0, -10, -3, 4, 2, 6, "secondary"),
    box(0, -9, 3, 4, 1, 1, "emissive"),
    // Beam housing
    framedBox(0, -12, 0, 4, 4, 6, "primary", "secondary"),
    // Long beam emitter shaft
    box(1, -11, 6, 2, 2, 16, "accent"),
    // Heat fins along the shaft (every 4 voxels)
    box(0, -12, 9, 4, 4, 1, "secondary"),
    box(0, -12, 13, 4, 4, 1, "secondary"),
    box(0, -12, 17, 4, 4, 1, "secondary"),
    // Charge core (emissive, bright)
    box(1, -11, 22, 2, 2, 2, "emissive"),
    // Targeting laser strip
    box(1, -8, 6, 2, 1, 16, "emissive"),
  ),
};

const armMissile: VoxelPart = {
  id: "arm_missile",
  anchor: { x: 16, y: 18, z: 0 },
  voxels: concat(
    // Shoulder mount (wider, low-profile)
    box(0, 0, -6, 6, 4, 12, "secondary"),
    // Rack housing
    framedBox(0, -4, -6, 6, 4, 12, "primary", "secondary"),
    // 3×4 = 12 missile tubes (front-facing)
    for12MissileTubes(0, -2, -5, 2, 2),
    // Front rack plate
    box(0, -4, 6, 6, 4, 1, "accent"),
    // Targeting array
    box(2, 0, -6, 2, 1, 1, "emissive"),
    // Tube emissive caps
    box(0, -2, 6, 6, 2, 1, "emissive"),
  ),
};

function for12MissileTubes(
  xOrigin: number,
  yOrigin: number,
  zFront: number,
  tubeW: number,
  tubeH: number,
): Voxel[] {
  const out: Voxel[] = [];
  const cols = 3;
  const rows = 4;
  const colStride = 2;
  const rowStride = 2;
  for (let c = 0; c < cols; c++) {
    for (let r = 0; r < rows; r++) {
      const x0 = xOrigin + c * colStride;
      const y0 = yOrigin + r * rowStride;
      // Tube wall (accent) — 1 voxel deep
      for (let xi = 0; xi < tubeW; xi++) {
        for (let yi = 0; yi < tubeH; yi++) {
          out.push({ x: x0 + xi, y: y0 + yi, z: zFront, slot: "accent" });
        }
      }
    }
  }
  return out;
}

const armBrawler: VoxelPart = {
  id: "arm_brawler",
  anchor: { x: 16, y: 16, z: 0 },
  voxels: concat(
    // Massive shoulder
    box(0, 0, -6, 6, 8, 12, "secondary"),
    box(0, 6, -6, 6, 2, 12, "primary"),
    // Thick upper arm
    framedBox(0, -8, -5, 6, 8, 10, "primary", "secondary"),
    // Elbow joint
    box(0, -10, -5, 6, 2, 10, "secondary"),
    box(0, -9, 5, 6, 1, 1, "emissive"),
    // Forearm — boxy
    framedBox(0, -16, -5, 6, 8, 10, "primary", "secondary"),
    // Wrist seam
    box(0, -18, -5, 6, 2, 10, "secondary"),
    // Closed-fist "knuckles" — four small protrusions (finger plating)
    box(1, -22, -4, 1, 4, 8, "secondary"),
    box(2, -22, -4, 1, 4, 8, "primary"),
    box(3, -22, -4, 1, 4, 8, "primary"),
    box(4, -22, -4, 1, 4, 8, "secondary"),
    // Knuckle accent ridge
    box(1, -22, 3, 4, 1, 1, "accent"),
    // Wrist emissive band
    box(0, -17, 5, 6, 1, 1, "emissive"),
  ),
};

// ─── Heads ─────────────────────────────────────────────────────────────────
// Anchored above the chassis shoulder block (chassis ends at y=24 for
// standard, head sits at y=28 from chassis local origin).

const headStandard: VoxelPart = {
  id: "head_standard",
  anchor: { x: 0, y: 28, z: 0 },
  voxels: concat(
    // Skull — chamfered cube
    chamferedBox(-3, 0, -3, 6, 6, 6, "primary"),
    // Side seams
    box(-3, 0, -3, 1, 6, 6, "secondary"),
    box(2, 0, -3, 1, 6, 6, "secondary"),
    // Visor — 2 emissive stripes
    box(-3, 2, 3, 6, 1, 1, "emissive"),
    box(-3, 4, 3, 6, 1, 1, "emissive"),
    // Visor divider
    box(-3, 3, 3, 6, 1, 1, "secondary"),
    // Top vent
    box(-1, 5, -2, 2, 1, 1, "secondary"),
    // Ear nubs
    box(-4, 2, -1, 1, 2, 2, "secondary"),
    box(3, 2, -1, 1, 2, 2, "secondary"),
  ),
};

const headSensor: VoxelPart = {
  id: "head_sensor",
  anchor: { x: 0, y: 28, z: 0 },
  voxels: concat(
    // Slimmer skull
    chamferedBox(-3, 0, -3, 6, 4, 6, "primary"),
    // Sensor crown
    box(-4, 4, -3, 8, 2, 6, "secondary"),
    box(-4, 5, -3, 8, 1, 6, "accent"),
    // Eye-slit — single thin emissive line
    box(-3, 1, 3, 6, 1, 1, "emissive"),
    // Pinhole sensors on the crown
    box(-3, 5, 2, 1, 1, 1, "emissive"),
    box(2, 5, 2, 1, 1, 1, "emissive"),
    // Tall tapered antenna
    box(0, 6, -2, 1, 4, 1, "secondary"),
    box(0, 10, -2, 1, 4, 1, "accent"),
    // Antenna tip (emissive)
    box(0, 14, -2, 1, 1, 1, "emissive"),
  ),
};

const headCockpit: VoxelPart = {
  id: "head_cockpit",
  anchor: { x: 0, y: 28, z: 0 },
  voxels: concat(
    // Forward-tilted canopy
    chamferedBox(-3, 0, -2, 6, 6, 8, "primary"),
    // Wraparound visor — 3 horizontal stripes at the front
    box(-3, 3, 4, 6, 1, 1, "emissive"),
    box(-3, 4, 5, 6, 1, 1, "emissive"),
    box(-3, 5, 5, 6, 1, 1, "emissive"),
    // Visor frame
    box(-3, 2, 4, 6, 1, 2, "secondary"),
    // Neck guard at back
    box(-3, 0, -3, 6, 2, 1, "secondary"),
    // Side optics
    box(-4, 3, 2, 1, 2, 2, "secondary"),
    box(3, 3, 2, 1, 2, 2, "secondary"),
  ),
};

// ─── Mount variants ───────────────────────────────────────────────────────

const mountNone: VoxelPart = {
  id: "mount_none",
  anchor: { x: 0, y: 0, z: 0 },
  voxels: [],
};

const mountAntenna: VoxelPart = {
  id: "mount_antenna",
  anchor: { x: -2, y: 24, z: -8 },
  voxels: concat(
    // Base plate
    box(0, 0, 0, 4, 1, 4, "secondary"),
    // Antenna shaft — tapered (3 segments of decreasing width)
    box(1, 1, 1, 2, 4, 2, "secondary"),
    box(1, 5, 1, 1, 4, 2, "accent"),
    box(1, 9, 1, 1, 3, 1, "accent"),
    // Tip beacon
    box(1, 12, 1, 1, 1, 1, "emissive"),
    // Side stub antennas
    box(0, 2, 0, 1, 1, 1, "secondary"),
    box(3, 2, 3, 1, 1, 1, "secondary"),
  ),
};

const mountShoulderRack: VoxelPart = {
  id: "mount_shoulder_rack",
  anchor: { x: -16, y: 22, z: 0 },
  voxels: concat(
    // Rack base
    box(0, 0, -6, 4, 4, 12, "secondary"),
    // 6 missile tubes (2×3 grid)
    box(0, 1, -5, 1, 1, 3, "accent"),
    box(0, 1, -1, 1, 1, 3, "accent"),
    box(0, 1, 3, 1, 1, 3, "accent"),
    box(0, 3, -5, 1, 1, 3, "accent"),
    box(0, 3, -1, 1, 1, 3, "accent"),
    box(0, 3, 3, 1, 1, 3, "accent"),
    // Tube emissive caps
    box(0, 1, -2, 1, 3, 1, "emissive"),
    box(0, 1, 2, 1, 3, 1, "emissive"),
    box(0, 1, 5, 1, 3, 1, "emissive"),
  ),
};

const mountReactorPack: VoxelPart = {
  id: "mount_reactor_pack",
  anchor: { x: 0, y: 16, z: -12 },
  voxels: concat(
    // Reactor canister — tall rounded box
    framedBox(-4, 0, 0, 8, 12, 4, "primary", "secondary"),
    // Glowing seam down the center
    box(0, 2, 0, 1, 8, 1, "emissive"),
    // Top vent
    box(-3, 12, 0, 6, 1, 4, "secondary"),
    box(-1, 12, 1, 2, 1, 2, "emissive"),
    // Side cooling fins
    box(-5, 4, 0, 1, 8, 4, "secondary"),
    box(4, 4, 0, 1, 8, 4, "secondary"),
    // Glow accent at the bottom (heat-soak ring)
    box(-4, 1, 0, 8, 1, 4, "emissive"),
  ),
};

export const PART_LIBRARY: PartLibrary = {
  chassis: [chassisStandard, chassisHeavy, chassisLight],
  legs: [legsBiped, legsReverseKnee, legsHeavy],
  left_arm: [armRifle, armCannon, armBeam, armMissile, armBrawler],
  right_arm: [armRifle, armCannon, armBeam, armMissile, armBrawler],
  head: [headStandard, headSensor, headCockpit],
  mount: [mountNone, mountAntenna, mountShoulderRack, mountReactorPack],
};
